import { AnalyticsContext } from '@carvana/eventable-clickstream';
import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/vdp-react-hooks';
import debounce from 'lodash.debounce';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Suggestion } from '~/contracts';
import {
  fetchSuggestionsApi,
  getFromLocalStorage,
  removeSuggestion,
  saveSuggestion,
  saveToLocalStorage
} from '~/services/suggestionService';
import IsServerSideRendering from '~/utilities/IsServerSideRendering';

type SuggestionContextType = {
  addSavedSuggestion: (_value: Suggestion) => void;
  clearSavedSuggestions: () => void;
  clearSuggestions: () => void;
  debouncedFetchSuggestions: (
    _value: string,
    _controller: AbortController
  ) => void;
  removeSavedSuggestion: (_value: Suggestion) => void;
  suggestions: LoadableState<Suggestion[]>;
  savedSuggestions: Suggestion[];
};

const defaultContext: SuggestionContextType = {
  addSavedSuggestion: _value => {},
  clearSavedSuggestions: () => {},
  clearSuggestions: () => {},
  debouncedFetchSuggestions: (_value, _controller) => {},
  removeSavedSuggestion: _value => {},
  suggestions: defaultLoadableState<Suggestion[]>([]),
  savedSuggestions: []
};

const SuggestionsContext = createContext<SuggestionContextType>(defaultContext);

export function SuggestionsContextProvider({ children }) {
  const suggestions = useLoadableState<Suggestion[]>([]);
  const { trackEvent } = useContext(AnalyticsContext);
  const [savedSuggestions, setSavedSuggestions] = useState<Suggestion[]>(
    IsServerSideRendering ? [] : getFromLocalStorage()
  );

  const addSavedSuggestion = useCallback(suggestion => {
    setSavedSuggestions(saveSuggestion(suggestion));
  }, []);

  const clearSavedSuggestions = useCallback(() => {
    setSavedSuggestions([]);
  }, []);

  const clearSuggestions = useCallback(() => {
    suggestions.setData([]);
  }, [suggestions]);

  const fetchSuggestions = useCallback(
    async (value, controller) => {
      const query = value.trim();

      if (!query) return;
      const data = await fetchSuggestionsApi(query, controller);

      if (!data) return;
      suggestions.setData(data);

      if (data.length === 0) {
        trackEvent('No suggestion results', { textEntered: query });
      }
    },
    [suggestions]
  );

  const debouncedFetchSuggestions = useCallback(
    debounce(fetchSuggestions, 250),
    []
  );

  const removeSavedSuggestion = useCallback(suggestion => {
    const newSuggestionsArray = removeSuggestion(suggestion);
    setSavedSuggestions(newSuggestionsArray);
  }, []);

  useEffect(() => {
    saveToLocalStorage(savedSuggestions);
  }, [savedSuggestions]);

  const value = useMemo<SuggestionContextType>(
    () => ({
      addSavedSuggestion,
      clearSavedSuggestions,
      clearSuggestions,
      debouncedFetchSuggestions,
      removeSavedSuggestion,
      suggestions,
      savedSuggestions
    }),
    [
      addSavedSuggestion,
      clearSavedSuggestions,
      clearSuggestions,
      debouncedFetchSuggestions,
      removeSavedSuggestion,
      suggestions,
      savedSuggestions
    ]
  );

  return (
    <SuggestionsContext.Provider value={value}>
      {children}
    </SuggestionsContext.Provider>
  );
}

SuggestionsContextProvider.displayName = 'SuggestionsContextProvider';

export default SuggestionsContext;
